'use client';

import AnimatedCounter from '@components/animations/AnimatedCounter';
import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { usePaymentAndOrder } from '../../payments/PaymentAndOrderContext';
import { parseHighlightedText } from './highlighters';
import SocialProofFeatureIcon from './socialProofFeatureIcons';

export default function GoalLandingPageSocialProofSection() {
  const { state } = usePaymentAndOrder();
  const accentColor = state.goal?.landingPage?.accentColor || DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  if (!state.goal?.landingPage?.socialProof) return <></>;

  return (
    <div
      className={classNames(
        'flex flex-col items-center w-full',
        setupForDark('bg-[#2c2c2c]'),
        // RESPONSIVE STYLES
        'gap-xl @[640px]:gap-huge',
        'p-xl @[640px]:p-huge'
      )}
    >
      <div
        className={classNames(
          'flex flex-col items-center w-full',
          // RESPONSIVE STYLES
          'gap-xl @[640px]:gap-xxxl',
          'max-w-[1200px]'
        )}
      >
        <div
          className={classNames(
            'flex flex-col gap-md items-start w-full text-center',
            setupForDark('text-white')
          )}
        >
          <h2
            className={classNames(
              'font-bold leading-[1.2] w-full',
              // RESPONSIVE STYLES
              'text-[28px] @[480px]:text-[32px] @[640px]:text-[48px] break-words'
            )}
          >
            {parseHighlightedText(
              state.goal?.landingPage?.socialProof?.title,
              'text-white',
              undefined,
              accentColor
            )}
          </h2>
          <p
            className={classNames(
              'font-normal leading-[1.3] w-full',
              // RESPONSIVE STYLES
              'text-[16px] @[640px]:text-[20px]'
            )}
          >
            {parseHighlightedText(
              state.goal?.landingPage?.socialProof?.subtitle,
              'text-white',
              undefined,
              accentColor
            )}
          </p>
        </div>

        <div
          className={classNames(
            'flex items-center justify-between w-full',
            // RESPONSIVE STYLES
            'flex-col gap-xl @[640px]:flex-row @[640px]:gap-none',
            'flex-wrap'
          )}
        >
          {state.goal?.landingPage?.socialProof?.statistics?.map((proof, index) => (
            <div key={index} className='flex flex-col gap-xs items-center justify-center flex-1'>
              <div className='flex gap-xxs items-center justify-center w-full overflow-hidden'>
                <div
                  className={classNames(
                    'font-semibold leading-[1.2] text-center',
                    'text-[40px] @[640px]:text-[60px]'
                  )}
                  style={{ color: accentColor }}
                >
                  +
                </div>
                <AnimatedCounter
                  value={proof.value}
                  className={classNames(
                    'font-semibold leading-[1.2]',
                    'text-[40px] @[640px]:text-[60px]'
                  )}
                  style={{ color: accentColor }}
                />
              </div>
              <div
                className={classNames(
                  'font-semibold leading-[1.1] text-center whitespace-nowrap',
                  setupForDark('text-white'),
                  'text-[16px] @[640px]:text-[20px]'
                )}
              >
                {parseHighlightedText(proof.label, 'text-white', undefined, accentColor)}
              </div>
            </div>
          ))}
        </div>

        <div
          className={classNames(
            'grid w-full gap-md mt-xxxl',
            // RESPONSIVE STYLES
            'grid-cols-1 @[640px]:grid-cols-2 @[1024px]:grid-cols-4'
          )}
        >
          {state.goal?.landingPage?.socialProof?.features?.map((proof, index) => (
            <div key={index} className='flex flex-row gap-sm items-center'>
              <SocialProofFeatureIcon icon={proof.icon} color={accentColor} />
              <div
                className={classNames(
                  'flex-1 font-normal leading-[1.2]',
                  setupForDark('text-white'),
                  'text-[16px] @[640px]:text-[20px]'
                )}
              >
                {parseHighlightedText(proof.text, 'text-white', undefined, accentColor)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
